import { useCallback, useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

import type { Map, MapPosition } from "@locators/components/shared/hooks/useFlotiqStores/useFlotiqStores.types";
import { getLatLngAndRadius } from "@locators/components/shared/utils";
import debounce from "lodash.debounce";

import { useConfig } from "@jti/app-context/useConfig";
import { FlotiqApi } from "@jti/rest-api";

import type { FlotiqEventStore } from "../types";
import { getEventsFilteredByDateRange, getTransformedEventMarkers } from "../utils";

const DEFAULT_NUMBER_OF_EVENTS = 1000;

type UseEventsStoresArgs = {
  storesLimit?: number;
  selectedDateRange?: DateRange;
};

export type UseEventsStoresReturn = {
  eventsStores: FlotiqEventStore[];
  activeStoreId: string;
  getEventsStores: (map: Map) => Promise<void> | void;
  handleSelectStore: (storeId: string) => void;
  getUserMarkerPosition: () => void;
  userMarkerPosition: { lat: number; lng: number } | undefined;
  mapPosition: MapPosition | undefined;
};

export const useEventsStores = (params?: UseEventsStoresArgs): UseEventsStoresReturn => {
  const { storesLimit = DEFAULT_NUMBER_OF_EVENTS, selectedDateRange } = params || {};
  const { FLOTIQ_API_URL, FLOTIQ_API_KEY } = useConfig();

  const [stores, setStores] = useState<FlotiqEventStore[]>([]);
  const [activeStoreId, setActiveStoreId] = useState<string>("");
  const [userMarkerPosition, setUserMarkerPosition] = useState<{ lat: number; lng: number }>();
  const [mapPosition, setMapPosition] = useState<MapPosition | undefined>();

  const flotiqApi = useMemo(() => new FlotiqApi(FLOTIQ_API_URL, FLOTIQ_API_KEY), [FLOTIQ_API_KEY, FLOTIQ_API_URL]);

  const handleLoadMarkersFromApi = useCallback(
    async (map: Map) => {
      const mapData = getLatLngAndRadius(map);

      if (!mapData) {
        return;
      }

      const { lat, lng, radius, zoom } = mapData;

      try {
        const response = await flotiqApi.getEvents({ radius, lat, lng, eventsLimit: storesLimit });
        if (!response.data) {
          return;
        }

        setMapPosition({ lat, lng, zoom });
        const transformedMarkers = getTransformedEventMarkers(response.data);
        setStores(transformedMarkers);
      } catch (e) {
        console.error(e);
      }
    },
    [flotiqApi, storesLimit]
  );

  const debouncedLoadMarkersFromApi = useMemo(
    () => debounce(handleLoadMarkersFromApi, 300),
    [handleLoadMarkersFromApi]
  );

  const filteredStores = useMemo(
    () => getEventsFilteredByDateRange(stores, selectedDateRange),
    [selectedDateRange, stores]
  );

  useEffect(() => () => debouncedLoadMarkersFromApi.cancel(), [debouncedLoadMarkersFromApi]);

  const handleSelectStore = useCallback((storeId: string) => {
    setActiveStoreId(storeId);
  }, []);

  const getUserMarkerPosition = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserMarkerPosition({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => {
        console.info("Geolocation permission denied or unavailable.");
      }
    );
  }, []);

  return {
    eventsStores: filteredStores,
    activeStoreId,
    getEventsStores: debouncedLoadMarkersFromApi,
    handleSelectStore,
    getUserMarkerPosition,
    userMarkerPosition,
    mapPosition,
  };
};
